import { useEffect, useMemo, useRef, useState } from 'react'

import startCase from 'lodash/startCase'
import { FiChevronDown, FiX } from 'react-icons/fi'

import { useMapContext } from '../MapContext'
import { getSeverityColor } from '../utils'

interface AlertsFilterProps {
  isOpen: boolean
  onToggle: () => void
}

type Severity = 'critical' | 'warning' | 'informational'

function IndeterminateCheckbox({
  checked,
  indeterminate,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { indeterminate: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate
    }
  }, [indeterminate])
  return <input type="checkbox" ref={ref} checked={checked} {...props} />
}

export const AlertsFilter = ({ isOpen, onToggle }: AlertsFilterProps) => {
  const context = useMapContext()
  const { filters, setFilters, filtersResetCount } = context
  const [alertSearch, setAlertSearch] = useState('')
  const [showSelectedAlerts, setShowSelectedAlerts] = useState(false)
  const [pendingActiveSince, setPendingActiveSince] = useState(filters.alertsActiveSince ?? '')
  const initializedKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    setPendingActiveSince(filters.alertsActiveSince ?? '')
  }, [filters.alertsActiveSince])

  const handleApplyActiveSince = () => {
    setFilters({ ...filters, alertsActiveSince: pendingActiveSince || null })
  }

  const handleClearActiveSince = () => {
    setPendingActiveSince('')
    setFilters({ ...filters, alertsActiveSince: null })
  }

  // Get all unique alert names with their severities from all assets
  const allAlerts = useMemo(() => {
    const alertMap = new Map<string, Severity>()
    context.allAssets.forEach((asset) => {
      asset.activeAlerts?.forEach((alert) => {
        const existing = alertMap.get(alert.name)
        if (!existing || severityRank(alert.severity) > severityRank(existing)) {
          alertMap.set(alert.name, alert.severity)
        }
      })
    })
    const alerts: { name: string; severity: Severity }[] = []
    alertMap.forEach((severity, name) => alerts.push({ name, severity }))
    return alerts.sort((a, b) => a.name.localeCompare(b.name))
  }, [context.allAssets])

  const severityCheckStates = useMemo(() => {
    const states: Record<Severity, { state: 'checked' | 'indeterminate' | 'unchecked'; count: number }> = {
      critical: { state: 'unchecked', count: 0 },
      warning: { state: 'unchecked', count: 0 },
      informational: { state: 'unchecked', count: 0 },
    }
    for (const sev of ['critical', 'warning', 'informational'] as const) {
      const alertsForSeverity = allAlerts.filter((a) => a.severity === sev)
      const count = alertsForSeverity.length
      if (count === 0) continue
      const selectedCount = alertsForSeverity.filter((a) => filters.alerts.has(a.name)).length
      const state = selectedCount === 0 ? 'unchecked' : selectedCount === count ? 'checked' : 'indeterminate'
      states[sev] = { state, count }
    }
    return states
  }, [allAlerts, filters.alerts])

  // Toggle all alerts of a severity
  const handleSeverityToggle = (severity: Severity) => {
    const alertsForSeverity = allAlerts.filter((a) => a.severity === severity)
    const newSet = new Set(filters.alerts)
    const allSelected = alertsForSeverity.every((a) => newSet.has(a.name))

    if (allSelected) {
      alertsForSeverity.forEach((a) => newSet.delete(a.name))
    } else {
      alertsForSeverity.forEach((a) => newSet.add(a.name))
    }
    setFilters({ ...filters, alerts: newSet })
  }

  // No Alerts checkbox
  const handleNoAlertsChange = (checked: boolean) => {
    setFilters({
      ...filters,
      alertSeverity: { ...filters.alertSeverity, noAlerts: checked },
    })
  }

  useEffect(() => {
    if (allAlerts.length === 0) return
    const initKey = `${filters.companyId}:${filtersResetCount}`
    if (initializedKeyRef.current === initKey) return
    initializedKeyRef.current = initKey
    const allNames = allAlerts.map((a) => a.name)
    setFilters((prev) => ({
      ...prev,
      alerts: new Set(allNames),
      alertSeverity: { ...prev.alertSeverity, noAlerts: true },
    }))
  }, [allAlerts, filtersResetCount, setFilters])

  const filteredAlerts = useMemo(() => {
    let alerts = allAlerts

    if (showSelectedAlerts && filters.alerts.size > 0) {
      alerts = alerts.filter((alert) => filters.alerts.has(alert.name))
    }

    if (alertSearch) {
      const searchLower = alertSearch.toLowerCase()
      alerts = alerts.filter((alert) => alert.name.toLowerCase().includes(searchLower))
    }
    return alerts
  }, [allAlerts, alertSearch, showSelectedAlerts, filters.alerts])

  const handleAlertToggle = (alertName: string, checked: boolean) => {
    const newSet = new Set(filters.alerts)
    if (checked) {
      newSet.add(alertName)
    } else {
      newSet.delete(alertName)
    }
    setFilters({ ...filters, alerts: newSet })
  }

  const handleClearSelected = () => {
    setFilters({ ...filters, alerts: new Set() })
    setShowSelectedAlerts(false)
  }

  const desktopFilterContent = (
    <div
      className="absolute top-full left-0 mt-1 min-w-[400px] max-w-[500px] shadow-lg bg-base-100 rounded-lg border border-base-300 z-20"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Severity checkboxes */}
      <div className="p-2 border-b border-base-300 flex items-center gap-4 whitespace-nowrap">
        {(['critical', 'warning', 'informational'] as const).map((severity) => {
          const { state, count } = severityCheckStates[severity]
          return (
            <label
              key={severity}
              className={`flex items-center gap-1.5 ${count === 0 ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
            >
              <IndeterminateCheckbox
                checked={state === 'checked'}
                indeterminate={state === 'indeterminate'}
                disabled={count === 0}
                onChange={() => handleSeverityToggle(severity)}
                className="checkbox checkbox-xs rounded-none"
              />
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getSeverityColor(severity) }} />
              <span className="text-xs" title={count === 0 ? `No ${severity} alerts active` : undefined}>
                {startCase(severity)} {count > 0 && `(${count})`}
              </span>
            </label>
          )
        })}
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.alertSeverity.noAlerts}
            onChange={(e) => handleNoAlertsChange(e.target.checked)}
            className="checkbox checkbox-xs rounded-none"
          />
          <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#6b7280' }} />
          <span className="text-xs">No Alerts</span>
        </label>
      </div>
      {/* Active Since date picker */}
      <div className="p-2 border-b border-base-300 flex items-center gap-2">
        <label className="text-xs whitespace-nowrap">Active Since</label>
        <input
          type="date"
          value={pendingActiveSince}
          onChange={(e) => setPendingActiveSince(e.target.value)}
          className="input input-bordered input-sm flex-1"
          max={new Date().toISOString().slice(0, 10)}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleApplyActiveSince()
          }}
          disabled={pendingActiveSince === (filters.alertsActiveSince ?? '')}
          className="btn btn-xs btn-primary"
        >
          Apply
        </button>
        {filters.alertsActiveSince && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearActiveSince()
            }}
            className="btn btn-ghost btn-xs btn-circle"
          >
            <FiX className="w-3 h-3" />
          </button>
        )}
      </div>
      {/* Search input */}
      <div className="p-2 border-b border-base-300">
        <div className="relative">
          <input
            type="text"
            placeholder="Search alerts..."
            value={alertSearch}
            onChange={(e) => setAlertSearch(e.target.value)}
            className="input input-bordered input-sm w-full pr-8"
            onClick={(e) => e.stopPropagation()}
          />
          {alertSearch && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setAlertSearch('')
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
              aria-label="Clear search"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* Toggle and Clear buttons */}
        <div className="grid grid-cols-3 items-center mt-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              const allNames = new Set(allAlerts.map((a) => a.name))
              setFilters({ ...filters, alerts: allNames })
            }}
            className="btn btn-xs btn-ghost justify-self-start"
          >
            Select All ({allAlerts.length})
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setShowSelectedAlerts(!showSelectedAlerts)
            }}
            className={`btn btn-xs justify-self-center ${showSelectedAlerts ? 'btn-primary' : 'btn-ghost'}`}
          >
            {showSelectedAlerts ? `Show All (${allAlerts.length})` : `Show Selected (${filters.alerts.size})`}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearSelected()
            }}
            disabled={filters.alerts.size === 0}
            className={`btn btn-xs btn-ghost justify-self-end ${filters.alerts.size === 0 ? 'text-gray-400' : ''}`}
          >
            Clear Selected
          </button>
        </div>
      </div>
      {/* Alerts list */}
      <div className="max-h-60 overflow-y-auto p-2">
        {context.isLoadingAssets ? (
          <div className="flex items-center justify-center p-4">
            <span className="loading loading-spinner loading-sm" />
            <span className="text-xs text-base-content/60 ml-2">Loading alerts...</span>
          </div>
        ) : showSelectedAlerts && filters.alerts.size === 0 ? (
          <div className="text-xs text-base-content/60 p-2 text-center">None Selected</div>
        ) : filteredAlerts.length === 0 ? (
          <div className="text-xs text-base-content/60 p-2 text-center">
            {alertSearch ? 'No alerts found' : 'No alerts available'}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredAlerts.map((alert) => (
              <label key={alert.name} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                <input
                  type="checkbox"
                  checked={filters.alerts.has(alert.name)}
                  onChange={(e) => handleAlertToggle(alert.name, e.target.checked)}
                  className="checkbox checkbox-sm rounded-none"
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-xs flex-1">{alert.name}</span>
                <div
                  className="w-3 h-3 rounded-sm border border-base-300"
                  style={{ backgroundColor: getSeverityColor(alert.severity) }}
                  title={alert.severity}
                />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const mobileFilterContent = (
    <div className="bg-base-100 rounded-lg">
      {/* Severity chips */}
      <div className="border-b border-base-300">
        <div className="text-sm text-base-content/60 mb-4">Select by severity</div>
        <div className="flex flex-wrap gap-4 mb-4">
          {(['critical', 'warning', 'informational'] as const).map((severity) => {
            const { state, count } = severityCheckStates[severity]
            return (
              <label
                key={severity}
                className={`flex items-center gap-1.5 py-2 px-4 rounded-full bg-base-200 has-checked:bg-black/70 has-checked:text-white ${
                  count === 0 ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                }`}
                title={count === 0 ? `No ${severity} alerts active` : undefined}
              >
                <IndeterminateCheckbox
                  checked={state === 'checked'}
                  indeterminate={state === 'indeterminate'}
                  disabled={count === 0}
                  onChange={() => handleSeverityToggle(severity)}
                  className="checkbox checkbox-xs rounded-none peer hidden"
                />
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getSeverityColor(severity) }} />
                <span className="text-xs">
                  {startCase(severity)} {count > 0 && `(${count})`}
                </span>
              </label>
            )
          })}
          <label className="flex items-center gap-1.5 cursor-pointer py-2 px-4 rounded-full bg-base-200 has-checked:bg-black/70 has-checked:text-white">
            <input
              type="checkbox"
              checked={filters.alertSeverity.noAlerts}
              onChange={(e) => handleNoAlertsChange(e.target.checked)}
              className="checkbox checkbox-xs rounded-none peer hidden"
            />
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#6b7280' }} />
            <span className="text-xs">No Alerts</span>
          </label>
        </div>
      </div>

      {/* Active Since date picker */}
      <div className="p-3 border-b border-base-300 flex items-center gap-2">
        <label className="text-sm whitespace-nowrap">Active Since</label>
        <input
          type="date"
          value={pendingActiveSince}
          onChange={(e) => setPendingActiveSince(e.target.value)}
          className="input input-bordered flex-1"
          max={new Date().toISOString().slice(0, 10)}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleApplyActiveSince()
          }}
          disabled={pendingActiveSince === (filters.alertsActiveSince ?? '')}
          className="btn btn-sm btn-primary"
        >
          Apply
        </button>
        {filters.alertsActiveSince && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearActiveSince()
            }}
            className="btn btn-ghost btn-xs btn-circle"
          >
            <FiX className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="p-3 border-b border-base-300">
        <input
          type="text"
          placeholder="Search alerts..."
          value={alertSearch}
          onChange={(e) => setAlertSearch(e.target.value)}
          className="input input-bordered w-full"
        />
        {/* Toggle and Clear buttons */}
        <div className="grid grid-cols-3 items-center mt-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              const allNames = new Set(allAlerts.map((a) => a.name))
              setFilters({ ...filters, alerts: allNames })
            }}
            className="btn btn-xs btn-ghost justify-self-start"
          >
            Select All ({allAlerts.length})
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setShowSelectedAlerts(!showSelectedAlerts)
            }}
            className={`btn btn-xs justify-self-center ${showSelectedAlerts ? 'btn-primary' : 'btn-ghost'}`}
          >
            {showSelectedAlerts ? `Show All (${allAlerts.length})` : `Show Selected (${filters.alerts.size})`}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearSelected()
            }}
            disabled={filters.alerts.size === 0}
            className={`btn btn-xs btn-ghost justify-self-end ${filters.alerts.size === 0 ? 'text-gray-400' : ''}`}
          >
            Clear Selected
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-72 overflow-y-auto">
        {filteredAlerts.map((alert) => (
          <label key={alert.name} className="flex items-center gap-3 px-4 py-3 active:bg-base-200">
            <input
              type="checkbox"
              checked={filters.alerts.has(alert.name)}
              onChange={(e) => handleAlertToggle(alert.name, e.target.checked)}
              className="checkbox checkbox-primary"
            />
            <span className="flex-1">{alert.name}</span>
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: getSeverityColor(alert.severity) }} />
          </label>
        ))}
      </div>
    </div>
  )

  return (
    <>
      {/* DESKTOP */}
      <div className="relative hidden md:block" data-filter-dropdown>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
          className="btn btn-sm btn-outline min-w-[160px] flex items-center justify-between [&::before]:hidden [&::after]:hidden font-normal"
        >
          <span>
            Active Alerts
            {filters.alerts.size > 0 && <span className="ml-2 text-primary">({filters.alerts.size})</span>}
          </span>
          <FiChevronDown
            className={`w-4 h-4 text-base-content/60 transition-transform duration-200 ml-auto ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 mt-2 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 z-50">
            {desktopFilterContent}
          </div>
        )}
      </div>

      {/* MOBILE */}
      <div className="md:hidden w-full">
        <details className="collapse collapse-arrow" name="alert-filter-det">
          <summary className="collapse-title text-xl ">Active Alerts</summary>
          <div className="collapse-content">{mobileFilterContent}</div>
        </details>
      </div>
    </>
  )
}

function severityRank(severity: Severity): number {
  switch (severity) {
    case 'critical':
      return 3
    case 'warning':
      return 2
    case 'informational':
      return 1
    default:
      return 0
  }
}
